import { CalendarIcon } from '@heroicons/react/24/outline';
import React from 'react';

import { DomainEventType } from '@/services/api/matterChangesService';
import { formatTaskDate } from '@/utils/taskDateUtils';

import {
  ActivityActions,
  ActivityBody,
  ActivityDescription,
  ActivityIcon,
  ActivityItem,
  ActivityLink,
  ActivityTitle,
} from '../activity-feed-v2';
import { getUserFromEvent } from '../activity-feed-v2/ActivityDiffList';

// TODO: Remove this interface after running `pnpm run generate:api`
// The backend already exposes TaskDueDateChangedEvent in the schema, but the
// frontend's generated types are out of sync. Regenerating requires fixing ~100
// unrelated type errors due to accumulated drift - should be done in a dedicated PR.
interface TaskDueDateChangedPayload {
  target_id: string;
  task: { id: string; name: string };
  old?: string | null;
  new?: string | null;
}

export interface TaskDueDateChangedEventProps {
  event: DomainEventType & { payload: TaskDueDateChangedPayload };
}

export const TaskDueDateChangedEvent: React.FC<
  TaskDueDateChangedEventProps
> = ({ event }) => {
  const { task, old: oldDueDate, new: newDueDate } = event.payload;
  const user = getUserFromEvent(event);

  const icon = (
    <ActivityIcon
      icon={
        <div className="flex h-6 w-6 items-center justify-center rounded-full bg-amber-100">
          <CalendarIcon className="h-4 w-4 text-amber-600" />
        </div>
      }
    />
  );

  const body = (
    <ActivityBody>
      {task?.name && <ActivityTitle>{task.name}</ActivityTitle>}
      <ActivityDescription>
        Due date changed
        {oldDueDate && ` from ${formatTaskDate(oldDueDate)}`}
        {newDueDate && ` to ${formatTaskDate(newDueDate)}`}
        {!oldDueDate && !newDueDate && ' (cleared)'}
      </ActivityDescription>
    </ActivityBody>
  );

  const actions = task?.id && (
    <ActivityActions>
      <ActivityLink
        href={`/matters/${event.matter_id}/updates?task_id=${task.id}`}
      >
        View Task
      </ActivityLink>
    </ActivityActions>
  );

  return (
    <ActivityItem
      icon={icon}
      title="Task Due Date Changed"
      timestamp={new Date(event.occurred_at ?? new Date())}
      user={user}
      body={body}
      actions={actions}
    />
  );
};
